/**
 * engine/mediasession.ts — the title and the buttons on a locked iPhone.
 *
 * Task 07, carried over from the old project. `spec.md` «Плеер»: «Экран блокировки (Media
 * Session): только как улучшение для iPhone … Все вызовы завёрнуты в защиту от ошибок;
 * положение и длительность передаются только при конечных корректных значениях. На Android
 * технология недоступна в Telegram — приложение это не скрывает и не обещает».
 *
 * `navigator.mediaSession` does not exist in the Android WebView Telegram draws Mini Apps in,
 * so every function here is a no-op that reports `false` rather than something the app relies
 * on; the mini player is the control surface everywhere. Whether the iOS card really appears
 * inside Telegram's own webview depends on Telegram's audio settings, which it does not
 * document — that is a manual check on the owner's phone, recorded in the verification log,
 * and until it is recorded no text in the app promises lock-screen control.
 *
 * ## Two ways this API bites
 *
 * 1. `setActionHandler` **throws** for an action the browser's list does not have. One
 *    `try` around the whole setup would stop at the first unknown action and leave `play`
 *    and `pause` unregistered on exactly the older phones. Every call has its own `try`.
 * 2. `setPositionState` **throws** when the duration is missing, `NaN` or negative, when the
 *    position is past the duration, or when the rate is `0` — and all four happen naturally:
 *    before the file opens, at the last microsecond of a track, on a stalled element.
 *    {@link syncPosition} refuses to call unless the values are provably valid.
 */

/** The shape used; looser than the DOM's on purpose — older iOS lacks `setPositionState`. */
export interface MediaSessionLike {
  metadata?: unknown;
  playbackState?: string;
  setActionHandler(
    action: string,
    handler:
      ((details: { readonly seekTime?: number; readonly seekOffset?: number }) => void) | null,
  ): void;
  setPositionState?(state?: {
    readonly duration?: number;
    readonly position?: number;
    readonly playbackRate?: number;
  }): void;
}

type MetadataConstructor = new (init: {
  title?: string;
  artist?: string;
  artwork?: readonly { src: string; sizes?: string; type?: string }[];
}) => unknown;

/** `navigator.mediaSession` when it exists, else `null`. Reading it can throw in a hardened webview. */
export function resolveMediaSession(): MediaSessionLike | null {
  try {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return null;
    const session = (navigator as unknown as { mediaSession?: unknown }).mediaSession;
    if (typeof session !== 'object' || session === null) return null;
    const candidate = session as MediaSessionLike;
    return typeof candidate.setActionHandler === 'function' ? candidate : null;
  } catch {
    return null;
  }
}

/** What the buttons do. Each is registered only when given: a dead button is worse than none. */
export interface MediaSessionHandlers {
  readonly play: () => void;
  readonly pause: () => void;
  readonly previoustrack?: () => void;
  readonly nexttrack?: () => void;
  readonly seekto: (timeS: number) => void;
  readonly seekbackward: (offsetS: number) => void;
  readonly seekforward: (offsetS: number) => void;
}

/** `seekOffset` may be absent; ten seconds is what every platform assumes. */
export const DEFAULT_SEEK_OFFSET_S = 10;

function offsetOf(details: { readonly seekOffset?: number }): number {
  const offset = details.seekOffset;
  return typeof offset === 'number' && Number.isFinite(offset) && offset > 0
    ? offset
    : DEFAULT_SEEK_OFFSET_S;
}

/**
 * Registers the buttons, one guarded call each. Returns the teardown, which is a no-op when
 * there is no session at all — every Android phone.
 */
export function setupMediaSession(
  session: MediaSessionLike | null,
  handlers: MediaSessionHandlers,
): () => void {
  if (session === null) return () => undefined;

  const table: [
    string,
    ((details: { seekTime?: number; seekOffset?: number }) => void) | undefined,
  ][] = [
    ['play', () => handlers.play()],
    ['pause', () => handlers.pause()],
    [
      'previoustrack',
      handlers.previoustrack === undefined ? undefined : () => handlers.previoustrack?.(),
    ],
    ['nexttrack', handlers.nexttrack === undefined ? undefined : () => handlers.nexttrack?.()],
    [
      'seekto',
      (details) => {
        const time = details.seekTime;
        if (typeof time === 'number' && Number.isFinite(time)) handlers.seekto(time);
      },
    ],
    ['seekbackward', (details) => handlers.seekbackward(offsetOf(details))],
    ['seekforward', (details) => handlers.seekforward(offsetOf(details))],
  ];

  const registered: string[] = [];
  for (const [action, handler] of table) {
    if (handler === undefined) continue;
    try {
      session.setActionHandler(action, handler);
      registered.push(action);
    } catch {
      // An older list of actions: this one is not available, the others still are.
    }
  }

  return () => {
    for (const action of registered) {
      try {
        session.setActionHandler(action, null);
      } catch {
        // Going away with the page anyway.
      }
    }
  };
}

export interface NowPlaying {
  readonly title: string;
  readonly artist: string;
  /** The cover, or the house label — never nothing. */
  readonly artwork: string;
}

/** Publishes the title card. `false` when there is nowhere to publish it. */
export function setNowPlaying(session: MediaSessionLike | null, meta: NowPlaying): boolean {
  if (session === null) return false;
  const Ctor = (globalThis as { MediaMetadata?: unknown }).MediaMetadata;
  // An object assigned without the constructor is silently ignored: a failure nobody sees.
  if (typeof Ctor !== 'function') return false;
  try {
    session.metadata = new (Ctor as MetadataConstructor)({
      title: meta.title,
      artist: meta.artist,
      artwork: [{ src: meta.artwork, sizes: '512x512' }],
    });
    return true;
  } catch {
    return false;
  }
}

/** Mirrors playing or paused onto the system. */
export function setPlaybackState(
  session: MediaSessionLike | null,
  state: 'none' | 'paused' | 'playing',
): boolean {
  if (session === null) return false;
  try {
    session.playbackState = state;
    return true;
  } catch {
    return false;
  }
}

/**
 * Pushes duration, position and rate — only when all three are provably valid. See the
 * header, point 2. The position is clamped, the rate falls back to 1.
 */
export function syncPosition(
  session: MediaSessionLike | null,
  durationS: number,
  positionS: number,
  rate = 1,
): boolean {
  if (session === null || typeof session.setPositionState !== 'function') return false;
  if (!Number.isFinite(durationS) || durationS <= 0) return false;
  const position = Number.isFinite(positionS) && positionS > 0 ? Math.min(positionS, durationS) : 0;
  const playbackRate = Number.isFinite(rate) && rate > 0 ? rate : 1;
  try {
    session.setPositionState({ duration: durationS, position, playbackRate });
    return true;
  } catch {
    return false;
  }
}
